import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/contexts/AuthContext";
import { useLanguage } from "@/contexts/LanguageContext";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { ThemeSwitcher } from "@/components/ThemeSwitcher";
import { Checkbox } from "@/components/ui/checkbox";
import { ChevronLeft, Mail, Lock, User, Phone, Eye, EyeOff, ArrowLeft, CheckCircle2 } from "lucide-react";

const Auth = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { signUp, signIn, signInWithOAuth, resetPassword, updatePassword, user } = useAuth();
  const { t } = useLanguage();
  const [loading, setLoading] = useState(false);
  const [mode, setMode] = useState<"signin" | "signup" | "forgot" | "reset" | "confirm-email" | "email-sent">("signin");
  const [showPassword, setShowPassword] = useState(false);
  const [resetEmail, setResetEmail] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [registeredEmail, setRegisteredEmail] = useState("");

  useEffect(() => {
    // Check if this is a password reset callback
    const modeParam = searchParams.get("mode");
    if (modeParam === "reset") {
      setMode("reset");
    }
  }, [searchParams]);

  useEffect(() => {
    if (user && mode !== "reset") {
      navigate("/");
    }
  }, [user, navigate, mode]);

  const [signUpData, setSignUpData] = useState({
    email: "",
    password: "",
    fullName: "",
    phone: "",
  });

  const [acceptTerms, setAcceptTerms] = useState(false);
  const [acceptPrivacy, setAcceptPrivacy] = useState(false);

  const [signInData, setSignInData] = useState({
    email: "",
    password: "",
  });

  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    const { error } = await signUp(
      signUpData.email,
      signUpData.password,
      signUpData.fullName,
      signUpData.phone
    );
    setLoading(false);
    if (!error) {
      setRegisteredEmail(signUpData.email);
      setMode("confirm-email");
    }
  };

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    const { error } = await signIn(signInData.email, signInData.password);
    setLoading(false);
    if (!error) {
      navigate("/");
    }
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    const { error } = await resetPassword(resetEmail);
    setLoading(false);
    if (!error) {
      setMode("email-sent");
    }
  };

  const handleUpdatePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      return;
    }
    if (newPassword.length < 6) {
      return;
    }
    setLoading(true);
    const { error } = await updatePassword(newPassword);
    setLoading(false);
    if (!error) {
      navigate("/");
    }
  };

  return (
    <div className="min-h-screen bg-background flex flex-col relative overflow-hidden">
      {/* Animated Background Blobs */}
      <div className="fixed inset-0 -z-10 overflow-hidden pointer-events-none">
        <div className="absolute top-10 -left-20 w-80 h-80 bg-ios-green/30 rounded-full blur-3xl animate-blob-fade-in animate-blob-fast" />
        <div className="absolute top-40 -right-20 w-96 h-96 bg-ios-blue/25 rounded-full blur-3xl animate-blob-fade-in-delay-1 animate-blob-fast animation-delay-2000" />
        <div className="absolute bottom-20 left-1/4 w-72 h-72 bg-ios-teal/20 rounded-full blur-3xl animate-blob-fade-in-delay-2 animate-blob-fast animation-delay-4000" />
      </div>

      {/* iOS Header - Fixed */}
      <header className="fixed top-0 left-0 right-0 z-40 flex items-center justify-between px-4 py-3 ios-glass border-b border-border/50">
        <Button 
          variant="ghost" 
          size="icon" 
          onClick={() => navigate("/")}
          className="h-9 w-9 rounded-full"
        >
          <ChevronLeft className="h-5 w-5" />
        </Button>
        <h1 className="text-lg font-semibold">
          {mode === "signin" ? t.login : t.signup}
        </h1>
        <div className="flex items-center gap-1">
          <ThemeSwitcher />
          <LanguageSwitcher />
        </div>
      </header>

      {/* Spacer for fixed header */}
      <div className="h-14" />

      <main className="flex-1 flex flex-col px-6 pt-6 pb-12 max-w-md mx-auto w-full">
        {/* Logo Section */}
        <div className="text-center mb-8">
          <div className="w-20 h-20 mx-auto mb-4 rounded-[22px] overflow-hidden shadow-lg animate-[scale-in_0.5s_ease-out] transition-all duration-300 hover:scale-110 hover:shadow-[0_0_30px_hsl(160_84%_50%/0.5)] cursor-pointer">
            <img 
              src="/favicon.png" 
              alt="Новкъаст GO" 
              className="w-full h-full object-cover"
            />
          </div>
          <h2 className="text-2xl font-bold tracking-tight mb-1 bg-gradient-to-r from-emerald-500 to-teal-500 bg-clip-text text-transparent animate-[fade-in_0.6s_ease-out_0.2s_both]">
            {mode === "signin" ? "С возвращением!" : 
             mode === "signup" ? "Добро пожаловать!" : 
             mode === "forgot" ? "Восстановление" : 
             mode === "reset" ? "Новый пароль" :
             mode === "confirm-email" ? "Проверьте почту" :
             "Письмо отправлено"}
          </h2>
          <p className="text-muted-foreground text-sm animate-[fade-in_0.6s_ease-out_0.3s_both]">
            {mode === "signin" ? "Войдите в свой аккаунт" : 
             mode === "signup" ? "Создайте новый аккаунт" : 
             mode === "forgot" ? "Введите email для восстановления" : 
             mode === "reset" ? "Придумайте новый пароль" :
             mode === "confirm-email" ? "Мы отправили письмо для подтверждения" :
             "Проверьте вашу почту"}
          </p>
        </div>

        {/* Mode Switcher - only show for signin/signup */}
        {(mode === "signin" || mode === "signup") && (
          <div className="flex gap-1 p-1 bg-secondary/80 backdrop-blur-sm rounded-2xl mb-6">
            <button
              onClick={() => setMode("signin")}
              className={`flex-1 py-3 rounded-xl text-sm font-medium transition-all ${
                mode === "signin" 
                  ? "bg-card shadow-md text-foreground" 
                  : "text-muted-foreground hover:text-foreground"
              }`}
            >
              {t.login}
            </button>
            <button
              onClick={() => setMode("signup")}
              className={`flex-1 py-3 rounded-xl text-sm font-medium transition-all ${
                mode === "signup" 
                  ? "bg-card shadow-md text-foreground" 
                  : "text-muted-foreground hover:text-foreground"
              }`}
            >
              {t.signup}
            </button>
          </div>
        )}

        {/* Back button for forgot/reset/confirm modes */}
        {(mode === "forgot" || mode === "reset" || mode === "confirm-email" || mode === "email-sent") && (
          <button
            onClick={() => setMode("signin")}
            className="flex items-center gap-2 text-ios-green font-medium mb-6 hover:underline"
          >
            <ArrowLeft className="h-4 w-4" />
            Назад к входу
          </button>
        )}

        {/* Sign In Form */}
        {mode === "signin" && (
          <form onSubmit={handleSignIn} className="space-y-4 animate-ios-fade">
            <div className="ios-card-elevated overflow-hidden">
              <div className="relative">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-ios-green" />
                <Input
                  type="email"
                  placeholder={t.email}
                  required
                  value={signInData.email}
                  onChange={(e) => setSignInData({ ...signInData, email: e.target.value })}
                  className="h-14 pl-12 pr-4 border-0 bg-transparent rounded-none text-base placeholder:text-muted-foreground focus-visible:ring-0"
                />
              </div>
              <div className="ios-separator" />
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-ios-green" />
                <Input
                  type={showPassword ? "text" : "password"}
                  placeholder={t.password}
                  required
                  value={signInData.password}
                  onChange={(e) => setSignInData({ ...signInData, password: e.target.value })}
                  className="h-14 pl-12 pr-12 border-0 bg-transparent rounded-none text-base placeholder:text-muted-foreground focus-visible:ring-0"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                >
                  {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>
            </div>

            <button 
              type="button" 
              onClick={() => setMode("forgot")}
              className="text-sm text-ios-green font-medium hover:underline"
            >
              Забыли пароль?
            </button>

            <Button
              type="submit"
              disabled={loading}
              className="w-full h-14 rounded-2xl text-base font-semibold bg-gradient-to-r from-ios-green to-ios-teal hover:opacity-90 shadow-lg transition-all"
            >
              {loading ? (
                <div className="h-5 w-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              ) : (
                t.login
              )}
            </Button>
          </form>
        )}

        {/* Sign Up Form */}
        {mode === "signup" && (
          <form onSubmit={handleSignUp} className="space-y-4 animate-ios-fade">
            <div className="ios-card-elevated overflow-hidden">
              <div className="relative">
                <User className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-ios-green" />
                <Input
                  type="text"
                  placeholder={t.fullName}
                  required
                  value={signUpData.fullName}
                  onChange={(e) => setSignUpData({ ...signUpData, fullName: e.target.value })}
                  className="h-14 pl-12 pr-4 border-0 bg-transparent rounded-none text-base placeholder:text-muted-foreground focus-visible:ring-0"
                />
              </div>
              <div className="ios-separator" />
              <div className="relative">
                <Phone className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-ios-green" />
                <Input
                  type="tel"
                  placeholder={t.phone}
                  required
                  value={signUpData.phone}
                  onChange={(e) => setSignUpData({ ...signUpData, phone: e.target.value })}
                  className="h-14 pl-12 pr-4 border-0 bg-transparent rounded-none text-base placeholder:text-muted-foreground focus-visible:ring-0"
                />
              </div>
              <div className="ios-separator" />
              <div className="relative">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-ios-green" />
                <Input
                  type="email"
                  placeholder={t.email}
                  required
                  value={signUpData.email}
                  onChange={(e) => setSignUpData({ ...signUpData, email: e.target.value })}
                  className="h-14 pl-12 pr-4 border-0 bg-transparent rounded-none text-base placeholder:text-muted-foreground focus-visible:ring-0"
                />
              </div>
              <div className="ios-separator" />
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-ios-green" />
                <Input
                  type={showPassword ? "text" : "password"}
                  placeholder={t.password}
                  required
                  minLength={6}
                  value={signUpData.password}
                  onChange={(e) => setSignUpData({ ...signUpData, password: e.target.value })}
                  className="h-14 pl-12 pr-12 border-0 bg-transparent rounded-none text-base placeholder:text-muted-foreground focus-visible:ring-0"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                >
                  {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>
            </div>

            {/* Agreement Checkboxes */}
            <div className="space-y-3">
              <label className="flex items-start gap-3 cursor-pointer">
                <Checkbox 
                  checked={acceptTerms}
                  onCheckedChange={(checked) => setAcceptTerms(checked === true)}
                  className="mt-0.5 data-[state=checked]:bg-ios-green data-[state=checked]:border-ios-green"
                />
                <span className="text-sm text-muted-foreground leading-tight">
                  Я принимаю{" "}
                  <button 
                    type="button"
                    onClick={() => navigate("/terms")}
                    className="text-ios-green hover:underline font-medium"
                  >
                    условия использования
                  </button>
                </span>
              </label>
              
              <label className="flex items-start gap-3 cursor-pointer">
                <Checkbox 
                  checked={acceptPrivacy}
                  onCheckedChange={(checked) => setAcceptPrivacy(checked === true)}
                  className="mt-0.5 data-[state=checked]:bg-ios-green data-[state=checked]:border-ios-green"
                />
                <span className="text-sm text-muted-foreground leading-tight">
                  Я согласен с{" "}
                  <button 
                    type="button"
                    onClick={() => navigate("/privacy")}
                    className="text-ios-green hover:underline font-medium"
                  >
                    политикой конфиденциальности
                  </button>
                </span>
              </label>
            </div>

            <Button
              type="submit"
              disabled={loading || !acceptTerms || !acceptPrivacy}
              className="w-full h-14 rounded-2xl text-base font-semibold bg-gradient-to-r from-ios-green to-ios-teal hover:opacity-90 shadow-lg transition-all disabled:opacity-50"
            >
              {loading ? (
                <div className="h-5 w-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              ) : (
                t.signup
              )}
            </Button>
          </form>
        )}

        {/* Forgot Password Form */}
        {mode === "forgot" && (
          <form onSubmit={handleForgotPassword} className="space-y-4 animate-ios-fade">
            <div className="ios-card-elevated overflow-hidden">
              <div className="relative">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-ios-green" />
                <Input
                  type="email"
                  placeholder="Введите ваш email"
                  required
                  value={resetEmail}
                  onChange={(e) => setResetEmail(e.target.value)}
                  className="h-14 pl-12 pr-4 border-0 bg-transparent rounded-none text-base placeholder:text-muted-foreground focus-visible:ring-0"
                />
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full h-14 rounded-2xl text-base font-semibold bg-gradient-to-r from-ios-green to-ios-teal hover:opacity-90 shadow-lg transition-all"
            >
              {loading ? (
                <div className="h-5 w-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              ) : (
                "Отправить ссылку"
              )}
            </Button>
          </form>
        )}

        {/* Reset Password Form */}
        {mode === "reset" && (
          <form onSubmit={handleUpdatePassword} className="space-y-4 animate-ios-fade">
            <div className="ios-card-elevated overflow-hidden">
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-ios-green" />
                <Input
                  type={showPassword ? "text" : "password"}
                  placeholder="Новый пароль"
                  required
                  minLength={6}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="h-14 pl-12 pr-12 border-0 bg-transparent rounded-none text-base placeholder:text-muted-foreground focus-visible:ring-0"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                >
                  {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>
              <div className="ios-separator" />
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-ios-green" />
                <Input
                  type={showPassword ? "text" : "password"}
                  placeholder="Подтвердите пароль"
                  required
                  minLength={6}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="h-14 pl-12 pr-4 border-0 bg-transparent rounded-none text-base placeholder:text-muted-foreground focus-visible:ring-0"
                />
              </div>
            </div>

            {newPassword && confirmPassword && newPassword !== confirmPassword && (
              <p className="text-sm text-destructive">Пароли не совпадают</p>
            )}

            <Button
              type="submit"
              disabled={loading || newPassword !== confirmPassword || newPassword.length < 6}
              className="w-full h-14 rounded-2xl text-base font-semibold bg-gradient-to-r from-ios-green to-ios-teal hover:opacity-90 shadow-lg transition-all disabled:opacity-50"
            >
              {loading ? (
                <div className="h-5 w-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              ) : (
                "Сохранить пароль"
              )}
            </Button>
          </form>
        )}

        {/* Email Confirmation Screen */}
        {mode === "confirm-email" && (
          <div className="text-center space-y-6 animate-ios-fade">
            <div className="w-24 h-24 mx-auto rounded-full bg-ios-green/10 flex items-center justify-center">
              <CheckCircle2 className="h-12 w-12 text-ios-green" />
            </div>
            <div className="space-y-2">
              <p className="text-base text-foreground">
                Мы отправили письмо на
              </p>
              <p className="text-lg font-semibold text-ios-green">
                {registeredEmail}
              </p>
              <p className="text-sm text-muted-foreground mt-4">
                Перейдите по ссылке в письме, чтобы подтвердить email и завершить регистрацию
              </p>
            </div>
            <Button
              onClick={() => setMode("signin")}
              className="w-full h-14 rounded-2xl text-base font-semibold bg-gradient-to-r from-ios-green to-ios-teal hover:opacity-90 shadow-lg transition-all"
            >
              Войти в аккаунт
            </Button>
          </div>
        )}

        {/* Email Sent Screen (for password reset) */}
        {mode === "email-sent" && (
          <div className="text-center space-y-6 animate-ios-fade">
            <div className="w-24 h-24 mx-auto rounded-full bg-ios-green/10 flex items-center justify-center">
              <Mail className="h-12 w-12 text-ios-green" />
            </div>
            <div className="space-y-2">
              <p className="text-base text-foreground">
                Письмо для восстановления отправлено на
              </p>
              <p className="text-lg font-semibold text-ios-green">
                {resetEmail}
              </p>
              <p className="text-sm text-muted-foreground mt-4">
                Перейдите по ссылке в письме, чтобы установить новый пароль
              </p>
            </div>
            <Button
              onClick={() => setMode("signin")}
              className="w-full h-14 rounded-2xl text-base font-semibold bg-gradient-to-r from-ios-green to-ios-teal hover:opacity-90 shadow-lg transition-all"
            >
              Вернуться к входу
            </Button>
          </div>
        )}

        {/* Divider - only show for signin/signup */}
        {(mode === "signin" || mode === "signup") && (
          <>
            <div className="flex items-center gap-4 my-6">
              <div className="flex-1 h-px bg-border" />
              <span className="text-xs text-muted-foreground">или</span>
              <div className="flex-1 h-px bg-border" />
            </div>

            {/* Social Login */}
            <div className="space-y-3">
              <button 
                onClick={() => signInWithOAuth('google')}
                className="w-full h-14 ios-card flex items-center justify-center gap-3 rounded-2xl ios-press hover:bg-secondary/50 transition-colors"
              >
                <svg className="h-5 w-5" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                <span className="font-medium">Войти через Google</span>
              </button>
            </div>

            {/* Footer */}
            <p className="text-center text-sm text-muted-foreground mt-6">
              {mode === "signin" ? "Нет аккаунта? " : "Уже есть аккаунт? "}
              <button 
                onClick={() => setMode(mode === "signin" ? "signup" : "signin")}
                className="text-ios-green font-semibold hover:underline"
              >
                {mode === "signin" ? t.signup : t.login}
              </button>
            </p>
          </>
        )}
      </main>
    </div>
  );
};

export default Auth;